import React from 'react';
import { css, Theme } from '@emotion/react';
import { breakpoints, BirdiTheme } from '@birdi/theme';
import { CloudHeaderV2 } from '../common/HeaderV2';
import SideNavigationDashboard from '../common/side-menu/SideNavigationDashboard';
import SideNavigationAdmin from '../common/side-menu/SideNavigationAdmin';
import SideNavigationUserProfile from '../common/side-menu/SideNavigationProfile';
import SideNavigationWorkspace from '../common/side-menu/SideNavigationWorkspace';
import SideNavigationPilot from '../common/side-menu/SideNavigationPilot';
import SideNavigationInspectionTools from '../common/side-menu/SideNavigationInspectionTools';
import { MobileDrpdownMenu } from '../common/side-menu/mobile-menu/dropdown-menu';
import { BackButtonV2 } from '../common/side-menu/side-bar-utils';
import { useMetaSelector } from '../meta/reducer';
import { TrialBanner } from '../dashboard-new/trial-banner';
import { MaintenanceBanner } from '../common/maintenance-banner';

export const layoutCSS = (theme: Theme) => css`
  /* Mobile layout */
  .banner-wrapper {
    width: 100%;
  }

  /* Grid paints content after header; raise header so mobile menu sits above dashboard tabs */
  .header-wrapper {
    position: relative;
    z-index: 200;
  }

  /* Desktop layout */
  @media (min-width: ${breakpoints.m}) {
    background-color: ${theme.sand};
    display: grid;
    grid-template-areas:
      'banner  banner'
      'header  header'
      'sidebar content';
    grid-template-columns: 340px 1fr;
    grid-template-rows: auto auto 1fr;
    column-gap: 8px;
    height: 100vh;
    .banner-wrapper {
      grid-area: banner;
    }
    .header-wrapper {
      grid-area: header;
    }
    .sidebar-wrapper {
      grid-area: sidebar;
      margin: 0 0 10px 10px;
      background-color: white;
      border-radius: 15px;
      overflow: hidden;
    }
    .content-wrapper {
      grid-area: content;
      min-width: 0;
      overflow-y: auto;
      background-color: white;
      border-radius: 15px;
      margin: 0 8px 10px 0;
      .mobile-menu {
        display: none;
      }
    }
  }
`;

export const MobileHeader = ({
  title,
  backToPage = '/',
}: {
  title: string;
  backToPage?: string;
}) => (
  <div>
    <div
      css={css`
        padding: 1em;
      `}
    >
      <BackButtonV2 backToPage={backToPage} />
    </div>
    <div
      css={(theme: BirdiTheme) => css`
        background-color: ${theme.mono20};
        width: 100%;
        min-height: 3.5em;
        font-weight: bold;
        display: flex;
        align-items: center;
      `}
    >
      <span
        css={css`
          margin-left: 1em;
        `}
      >
        {title}
      </span>
    </div>
  </div>
);

export interface SidebarLayoutProps {
  sidebar:
    | 'dashboard'
    | 'admin'
    | 'user-profile'
    | 'workspace'
    | 'pilot-settings'
    | 'inspection-tools';
  children: React.ReactNode;
}

export const SidebarLayout = ({ sidebar, children }: SidebarLayoutProps) => {
  let sidebarComponent;
  let mobileMenu = <></>;
  let mobileHeader = <></>;

  const meta = useMetaSelector();
  const isWorkspaceAdmin = meta.permissions.includes('workspace:user:create');

  switch (sidebar) {
    case 'dashboard':
      sidebarComponent = <SideNavigationDashboard />;
      break;
    case 'admin':
      sidebarComponent = <SideNavigationAdmin />;
      mobileHeader = <MobileHeader title="Administration" />;
      mobileMenu = <MobileDrpdownMenu title="admin" />;
      break;
    case 'user-profile':
      sidebarComponent = <SideNavigationUserProfile />;
      mobileHeader = (
        <MobileHeader
          title="User Details"
          backToPage={isWorkspaceAdmin ? '/admin-users' : '/'}
        />
      );
      mobileMenu = <MobileDrpdownMenu title="user-profile" />;
      break;
    case 'workspace':
      sidebarComponent = <SideNavigationWorkspace />;
      break;
    case 'pilot-settings':
      sidebarComponent = <SideNavigationPilot />;
      mobileHeader = <MobileHeader title="Pilot Settings" />;
      mobileMenu = <MobileDrpdownMenu title="pilot-settings" />;
      break;
    case 'inspection-tools':
      sidebarComponent = <SideNavigationInspectionTools />;
      mobileHeader = <MobileHeader title="Inspection Tools" />;
      mobileMenu = <MobileDrpdownMenu title="inspection-tools" />;
      break;
    default:
      sidebarComponent = <SideNavigationDashboard />;
      break;
  }

  return (
    <main css={layoutCSS}>
      <div className="banner-wrapper">
        <MaintenanceBanner />
        <TrialBanner />
      </div>
      <div className="header-wrapper">
        <CloudHeaderV2 />
      </div>
      <div className="sidebar-wrapper">{sidebarComponent}</div>
      <div className="content-wrapper">
        <div className="mobile-menu">
          {mobileHeader}
          {mobileMenu}
        </div>
        {children}
      </div>
    </main>
  );
};
